import { ArrowSquareOutIcon, ImageIcon, LockSimpleIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useEffectiveLockedFields } from '@/hooks/useEffectiveLockedFields';
import { normalizeLockKey } from '@/lib/brand/resolve-brand-kit-chain';
import { cn } from '@/lib/utils';
import type { BrandKit } from '@/types/brand-kit';

interface LogoVariantPickerProps {
  brandKit: BrandKit | null;
  onPick: (variant: 'primary' | 'light' | 'dark', logoUrl: string) => void;
  disabled?: boolean;
}

type Variant = 'primary' | 'light' | 'dark';

const VARIANTS: Array<{ key: Variant; label: string; urlKey: keyof BrandKit }> = [
  { key: 'primary', label: 'Primary logo', urlKey: 'logo_url' },
  { key: 'light', label: 'Light logo', urlKey: 'logo_light_url' },
  { key: 'dark', label: 'Dark logo', urlKey: 'logo_dark_url' },
];

export function LogoVariantPicker({ brandKit, onPick, disabled }: LogoVariantPickerProps) {
  const [open, setOpen] = useState(false);

  // BI-5-03: when logoUrl is INHERITED-locked (ancestor corporate lock), the
  // picker is constrained to the locked corporate logo — the child kit cannot
  // substitute a variant. Empty inherited set → picker behaves exactly as
  // before (consumer/corporate-own kits see zero change). The locked value
  // (`lockedValues.logoUrl`) wins over any raw child-kit `brandKit.logo_url`.
  const { inheritedLockedFields, lockedValues } = useEffectiveLockedFields();
  const logoLocked = inheritedLockedFields.map(normalizeLockKey).includes('logoUrl');
  const lockedLogoUrl = logoLocked ? (lockedValues.logoUrl ?? null) : null;

  const hasAnyLogo =
    brandKit && (brandKit.logo_url || brandKit.logo_light_url || brandKit.logo_dark_url);

  const handlePick = (variant: Variant, url: string) => {
    onPick(variant, url);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Add logo"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-caption font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ImageIcon className="size-3.5" weight="regular" />
          Add logo
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="start"
        className="w-64 p-1.5"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {logoLocked && lockedLogoUrl ? (
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => handlePick('primary', lockedLogoUrl)}
              className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted/60 text-foreground/85"
            >
              <div className="size-10 shrink-0 rounded border border-border/60 overflow-hidden bg-[image:repeating-conic-gradient(hsl(var(--muted))_0%_25%,hsl(var(--background))_0%_50%)] bg-[length:8px_8px]">
                <img
                  src={lockedLogoUrl}
                  alt="Corporate logo"
                  className="size-full object-contain"
                  crossOrigin="anonymous"
                />
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-detail font-medium leading-tight">Corporate logo</span>
                <span className="inline-flex items-center gap-1 text-micro text-muted-foreground">
                  <LockSimpleIcon className="size-3" weight="regular" />
                  Logo locked by your brand kit
                </span>
              </div>
            </button>
          </div>
        ) : !hasAnyLogo ? (
          <div className="flex flex-col gap-1.5 px-2 py-2">
            <p className="text-detail text-muted-foreground">No logos in your brand kit.</p>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                window.dispatchEvent(new CustomEvent('mora:openSettings', { detail: 'brand-kit' }));
              }}
              className="inline-flex items-center gap-1 text-detail text-foreground/70 hover:text-foreground self-start"
            >
              <ArrowSquareOutIcon className="size-3" weight="regular" />
              Open Brand Kit settings
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {VARIANTS.map(({ key, label, urlKey }) => {
              const url = brandKit?.[urlKey] as string | null | undefined;
              const hasLogo = Boolean(url);

              return (
                <button
                  key={key}
                  type="button"
                  disabled={!hasLogo}
                  onClick={() => hasLogo && url && handlePick(key, url)}
                  className={cn(
                    'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors',
                    hasLogo
                      ? 'hover:bg-muted/60 text-foreground/85'
                      : 'cursor-not-allowed text-muted-foreground/50',
                  )}
                >
                  <div
                    // Dark-mode-aware checkerboard. The previous hardcoded
                    // #e0e0e0 / #ffffff pattern read as a bright square in dark
                    // mode against the popover bg. Using muted tokens keeps the
                    // "transparent canvas" intent and adapts to theme.
                    className="size-10 shrink-0 rounded border border-border/60 overflow-hidden bg-[image:repeating-conic-gradient(hsl(var(--muted))_0%_25%,hsl(var(--background))_0%_50%)] bg-[length:8px_8px]"
                  >
                    {url && (
                      <img
                        src={url}
                        alt={label}
                        className="size-full object-contain"
                        crossOrigin="anonymous"
                      />
                    )}
                  </div>

                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-detail font-medium leading-tight">{label}</span>
                    {!hasLogo && (
                      <span className="text-micro text-muted-foreground/60">Not set</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
